var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Combobox as HeadlessUiCombobox } from '@headlessui/react';
import { CaretDown } from '@phosphor-icons/react';
import cx from 'clsx';
import { visuallyHidden } from '../../css/accessibility.css';
import { Input } from '../input/Input';
import { Stack } from '../stack/Stack';
import { DesktopDropdown } from '../selectbox/SelectBox';
import { Check } from '../checkbox/Checkbox';
import { labelStyles as inputLabelStyles } from '../input/Input.css';
import { labelStyles } from '../input_label/InputLabel.css';
import { caretVariants, caretWrapperVariants, checkVariants, optionActiveStyles, optionBaseVariants, optionSeparatorStyles, optionsWrapperStyles, positioningStyles, selectedStyles, wrapperStyles, } from '../selectbox/SelectBox.css';
import { buttonStyles, inputStyles, noMatchingStyles } from './ComboBox.css';
const compareSelectedValue = (a, b) => {
    if (typeof a === 'object' && typeof b === 'object') {
        return (a === null || a === void 0 ? void 0 : a.value) === (b === null || b === void 0 ? void 0 : b.value);
    }
    return a === b;
};
export function ComboBox(_a) {
    var { autoComplete = 'off', className, 'data-testid': testId, descriptionId, errorId, formatSelectedOption, hasError = false, isDisabled = false, isReadOnly = false, label, labelRef, name, noMatchLabel, onBlur, onChange, onFilter, onFocus, options, value = '', shouldVisuallyHideLabel = false } = _a, rest = __rest(_a, ["autoComplete", "className", 'data-testid', "descriptionId", "errorId", "formatSelectedOption", "hasError", "isDisabled", "isReadOnly", "label", "labelRef", "name", "noMatchLabel", "onBlur", "onChange", "onFilter", "onFocus", "options", "value", "shouldVisuallyHideLabel"]);
    const [query, setQuery] = React.useState('');
    const buttonRef = React.useRef(null);
    const buttonDownStateRef = React.useRef(false);
    React.useEffect(() => {
        const triggerButton = buttonRef.current;
        const onDown = () => {
            buttonDownStateRef.current = true;
        };
        const onUp = () => {
            buttonDownStateRef.current = false;
        };
        if (triggerButton) {
            triggerButton.addEventListener('mousedown', onDown);
            triggerButton.addEventListener('mouseup', onUp);
        }
        return () => {
            if (triggerButton) {
                triggerButton.removeEventListener('mousedown', onDown);
            }
        };
    }, []);
    const selectedOptionDefaultFormatter = (option) => { var _a; return (_a = option === null || option === void 0 ? void 0 : option.label) !== null && _a !== void 0 ? _a : ''; };
    const selectedOptionFormatter = formatSelectedOption !== null && formatSelectedOption !== void 0 ? formatSelectedOption : selectedOptionDefaultFormatter;
    const optionsDefaultFilter = (allOptions, q) => allOptions.filter((option) => option.label.toLowerCase().includes(q.toLowerCase()));
    const handleOnChange = (selectedOption) => {
        onChange(selectedOption);
        setQuery('');
    };
    const handleOnBlur = (event) => {
        const isExpanded = event.target.getAttribute('aria-expanded') === 'true';
        if (onBlur && !buttonDownStateRef.current) {
            if (!isExpanded || (isExpanded && event.relatedTarget === null)) {
                setQuery('');
                onBlur(event);
            }
        }
    };
    const filterFunc = onFilter !== null && onFilter !== void 0 ? onFilter : optionsDefaultFilter;
    const filteredOptions = query === '' ? options : filterFunc(options, query);
    return (_jsx("div", { className: cx(wrapperStyles, className), children: _jsx(HeadlessUiCombobox, { by: compareSelectedValue, disabled: isDisabled || isReadOnly, name: name, onChange: handleOnChange, value: value, children: ({ open }) => {
                return (_jsxs(Stack, { className: positioningStyles, spacing: 4, children: [_jsx(HeadlessUiCombobox.Label, { className: cx(labelStyles, inputLabelStyles, shouldVisuallyHideLabel && visuallyHidden), ref: labelRef, children: label }), _jsxs("div", { style: { position: 'relative' }, children: [_jsx(HeadlessUiCombobox.Input, Object.assign({}, rest, { as: Input, autoComplete: autoComplete, className: inputStyles, "data-displayvalue": selectedOptionFormatter(value), "data-testid": testId, descriptionId: descriptionId, displayValue: selectedOptionFormatter, errorId: errorId, hasError: hasError, isDisabled: isDisabled, isReadOnly: isReadOnly, onBlur: handleOnBlur, onChange: (event) => setQuery(event.target.value), onFocus: onFocus })), _jsx(HeadlessUiCombobox.Button, { className: cx(buttonStyles, caretWrapperVariants[open ? 'open' : 'default']), "data-testid": testId ? `${testId}-button` : undefined, ref: buttonRef, children: _jsx(CaretDown, { className: caretVariants[open ? 'open' : 'default'], weight: "light" }) })] }), open && (_jsx(DesktopDropdown, { children: filteredOptions.length < 1 ? (_jsx("span", { className: noMatchingStyles, children: noMatchLabel })) : (_jsx(HeadlessUiCombobox.Options, { className: optionsWrapperStyles, static: true, children: filteredOptions.map((option) => (_jsx(HeadlessUiCombobox.Option, { as: React.Fragment, value: option, children: ({ active, selected }) => (_jsx("li", { className: cx(optionSeparatorStyles, optionBaseVariants.default, selected && selectedStyles, active && optionActiveStyles), "data-testid": "etiDropdownOption", children: _jsxs(_Fragment, { children: [selected && _jsx(Check, { className: checkVariants.default }), option.label] }) })) }, option.value))) })) }))] }));
            } }) }));
}
